/**
 * UnverifiedQueuePage — Source-filtered FIFO queue of leads needing verification.
 *
 * Apr 28, 2026 (Squad-D). Screen 02 of 12.
 *
 * - Source filter chip row (All, ChatKart, IndiaMART, JustDial, Website, Field, Referral)
 * - Each row inline-expands to show ChatKart transcript + IndiaMART payload
 * - Quick actions per row: Call, Verify, Reject, Open detail
 *
 * Backend:
 *  - GET /leads?verificationStatus=pending_verification[&source=...]
 *  - Verify/Reject buttons go to detail / verify page; bulk verify is a follow-up.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Check, X, ChevronDown, Zap } from 'lucide-react';
import { api } from '@/utils/api';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import {
  PageShell,
  KbdHint,
  PriorityDot,
  SourceIcon,
  StatusChip,
  Panel,
  EmptyState,
  type Lead,
  unwrapList,
  leadName,
  leadPhone,
  leadProduct,
  leadRegion,
  priorityFrom,
  relativeTime,
  SOURCES,
} from './_lmShared';

const SOURCE_KEYS: { key: string; label: string }[] = [
  { key: 'all', label: 'All sources' },
  { key: 'chatkart', label: 'ChatKart' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'indiamart', label: 'IndiaMART' },
  { key: 'justdial', label: 'JustDial' },
  { key: 'website', label: 'Website' },
  { key: 'field', label: 'Field' },
  { key: 'referral', label: 'Referral' },
];

export function UnverifiedQueuePage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSource, setActiveSource] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true); // Oldest first

  useEffect(() => {
    setLoading(true);
    const sourceParam = activeSource === 'all' ? '' : `&sourceName=${activeSource}`;
    // Apr 28 evening — `scope=unverified` covers verificationStatus IN
    // (pending_verification, chatkart_verifying, needs_manual_verify).
    // Includes leads still in Avika's flow + leads stuck mid-conversation.
    safeGet<unknown>(
      `/lead-integrations/lead-imports?scope=unverified&limit=100${sourceParam}`,
    ).then((r) => {
      const raw = (r.data as any)?.imports ?? r.data;
      setRows(unwrapList<Lead>(raw));
      setError(r.error);
      setLoading(false);
    });
  }, [activeSource]);

  /* Counts per source for the filter chips. */
  const sourceCounts = useMemo(() => {
    const m: Record<string, number> = { all: rows.length };
    rows.forEach((l) => {
      const s = (l.source ?? 'unknown').toLowerCase();
      m[s] = (m[s] ?? 0) + 1;
    });
    return m;
  }, [rows]);

  /* Apply client-side sort. Backend should already FIFO. */
  const sorted = useMemo(() => {
    const list = [...rows];
    list.sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sortAsc ? ta - tb : tb - ta;
    });
    return list;
  }, [rows, sortAsc]);

  /* Keyboard V → jump to verify the oldest lead. */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.target as HTMLElement)?.tagName === 'INPUT' || (e.target as HTMLElement)?.tagName === 'TEXTAREA') return;
      if (e.key === 'v' || e.key === 'V') {
        e.preventDefault();
        if (sorted.length > 0) navigate(`/lead-manager/verify/${sorted[0].id}`);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, sorted]);

  async function handleVerify(leadId: string) {
    navigate(`/lead-manager/verify/${leadId}`);
  }

  async function handleReject(leadId: string) {
    if (!confirm('Reject this lead?')) return;
    try {
      await api.post(`/leads/${leadId}/verify`, { decision: 'rejected' });
      setRows((prev) => prev.filter((r) => r.id !== leadId));
    } catch (err) {
      alert('Reject failed — backend not yet ready, or you do not have permission.');
    }
  }

  return (
    <PageShell
      title="Unverified queue"
      sub={`${sorted.length} lead${sorted.length === 1 ? '' : 's'} · sorted ${sortAsc ? 'oldest' : 'newest'} first`}
      right={
        <>
          <button className="btn btn-outline" onClick={() => setSortAsc((s) => !s)}>
            Sort: {sortAsc ? 'Oldest' : 'Newest'} first <ChevronDown size={12} />
          </button>
          <button
            className="btn btn-primary"
            disabled={sorted.length === 0}
            onClick={() => sorted[0] && navigate(`/lead-manager/verify/${sorted[0].id}`)}
          >
            <Zap size={13} /> Run loop <KbdHint k="V" />
          </button>
        </>
      }
      scroll={false}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {/* Filter chips */}
        <div
          style={{
            padding: '12px 24px',
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            background: 'var(--ivory-1)',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
              fontWeight: 500,
              marginRight: 4,
            }}
          >
            Filter
          </div>
          {SOURCE_KEYS.map((f) => {
            const count = f.key === 'all' ? rows.length : sourceCounts[f.key] ?? 0;
            const active = activeSource === f.key;
            return (
              <button
                key={f.key}
                className="btn btn-sm"
                onClick={() => setActiveSource(f.key)}
                style={{
                  background: active ? 'var(--ink-0)' : 'var(--ivory-1)',
                  color: active ? 'var(--ivory-0)' : 'var(--ink-2)',
                  border: '1px solid ' + (active ? 'var(--ink-0)' : 'var(--line)'),
                }}
              >
                {f.label}
                {count > 0 && (
                  <span className="num" style={{ opacity: 0.6, marginLeft: 4 }}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            Showing {sorted.length} of {rows.length}
          </span>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {loading ? (
            <EmptyState title="Loading…" />
          ) : error || sorted.length === 0 ? (
            <EmptyState
              title={error ? 'Verification queue not available yet' : 'Inbox is clear'}
              sub={
                error
                  ? "Backend endpoint isn't online. Squad-A is shipping the schema; once live, leads will appear here."
                  : 'Every inbound lead has been processed. Check back soon.'
              }
            />
          ) : (
            sorted.map((l) => (
              <LeadRow
                key={l.id}
                l={l}
                expanded={expandedId === l.id}
                onToggle={() => setExpandedId(expandedId === l.id ? null : l.id)}
                onVerify={() => handleVerify(l.id)}
                onReject={() => handleReject(l.id)}
                onOpenDetail={() => navigate(`/lead-manager/leads/${l.id}`)}
              />
            ))
          )}
        </div>
      </div>
    </PageShell>
  );
}

function LeadRow({
  l,
  expanded,
  onToggle,
  onVerify,
  onReject,
  onOpenDetail,
}: {
  l: Lead;
  expanded: boolean;
  onToggle: () => void;
  onVerify: () => void;
  onReject: () => void;
  onOpenDetail: () => void;
}) {
  return (
    <div style={{ borderBottom: '1px solid var(--ivory-3)' }}>
      <div
        onClick={onToggle}
        style={{
          display: 'grid',
          gridTemplateColumns: '12px 22px 1.5fr 1.2fr 1fr 0.8fr 90px auto',
          gap: 14,
          alignItems: 'center',
          padding: '12px 24px',
          cursor: 'pointer',
          background: expanded ? 'var(--ivory-2)' : 'transparent',
        }}
      >
        <PriorityDot p={priorityFrom(l)} />
        <SourceIcon source={l.source} size={20} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontWeight: 500,
              fontSize: 13,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {leadName(l)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 1 }}>
            <span className="mono">{l.id.slice(-6)}</span>
            {(l.notes || l.note) && <> · {l.notes || l.note}</>}
          </div>
        </div>
        <span className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
          {leadPhone(l)}
        </span>
        <div style={{ fontSize: 12 }}>
          <div>{leadProduct(l)}</div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{leadRegion(l)}</div>
        </div>
        <StatusChip status={l.verificationStatus ?? l.status ?? 'pending'} />
        <span style={{ fontSize: 11, color: 'var(--ink-3)', fontVariantNumeric: 'tabular-nums' }}>
          {relativeTime(l.createdAt)}
        </span>
        <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-sm btn-primary" onClick={onVerify}>
            <Phone size={11} /> Verify
          </button>
          <button className="btn btn-sm btn-outline" onClick={onReject} title="Reject">
            <X size={12} />
          </button>
        </div>
      </div>
      {expanded && (
        <div
          style={{
            padding: '14px 24px 18px',
            background: 'var(--ivory-2)',
            borderTop: '1px solid var(--ivory-3)',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 16 }}>
            <Panel title="Source data" sub={`Source: ${SOURCES[(l.source ?? 'unknown').toLowerCase()]?.label ?? 'Unknown'}`}>
              <SourcePayload lead={l} />
            </Panel>
            <Panel title="Quick verify" sub="Decision after quick call">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12, color: 'var(--ink-2)' }}>
                <div>Phone: <span className="mono">{leadPhone(l)}</span></div>
                <div>Product: <span style={{ fontWeight: 500 }}>{leadProduct(l)}</span></div>
                <div>Region: {leadRegion(l)}</div>
                {l.notes || l.note ? (
                  <div style={{ marginTop: 4, padding: 8, background: 'var(--ivory-0)', borderRadius: 6, border: '1px solid var(--line)' }}>
                    {l.notes || l.note}
                  </div>
                ) : null}
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button className="btn btn-primary btn-sm" onClick={onVerify}>
                    <Check size={11} /> Open verify
                  </button>
                  <button className="btn btn-outline btn-sm" onClick={onReject}>
                    <X size={11} /> Reject
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={onOpenDetail}>
                    Open detail →
                  </button>
                </div>
              </div>
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}

function SourcePayload({ lead }: { lead: Lead }) {
  const fields: [string, string][] = [
    ['Lead ID', lead.id],
    ['Source', lead.source ?? '—'],
    ['Phone', leadPhone(lead)],
    ['Email', lead.email ?? '—'],
    ['Product interest', leadProduct(lead)],
    ['Region', leadRegion(lead)],
    ['Conversation state', lead.conversationState ?? '—'],
    ['Created', lead.createdAt ?? '—'],
  ];
  return (
    <div style={{ fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--ink-1)', lineHeight: 1.7 }}>
      {fields.map(([k, v]) => (
        <div
          key={k}
          style={{
            display: 'flex',
            gap: 10,
            borderBottom: '1px dashed var(--ivory-3)',
            padding: '3px 0',
          }}
        >
          <span style={{ width: 130, color: 'var(--ink-3)', flexShrink: 0 }}>{k}</span>
          <span style={{ flex: 1, wordBreak: 'break-word' }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

export default UnverifiedQueuePage;
